import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import bingoItemsData from './bingoItems.json';

function App() {
  const [gridSize, setGridSize] = useState(5);
  const [verificationCode, setVerificationCode] = useState('');
  const [bingoGrid, setBingoGrid] = useState([]);
  const [selectedCells, setSelectedCells] = useState([]);
  const [bingoLines, setBingoLines] = useState([]);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [verificationInput, setVerificationInput] = useState('');
  const [verificationResult, setVerificationResult] = useState({ message: '', isSuccess: false });
  const [showCelebration, setShowCelebration] = useState(false);
  const [gridContainerSize, setGridContainerSize] = useState({ width: 0, height: 0 });
  
  const bingoGridRef = useRef(null);
  const bingoContainerRef = useRef(null);
  const gridWrapperRef = useRef(null);
  const verificationInputRef = useRef(null);

  // 从JSON文件导入的Bingo项目列表
  const bingoItems = bingoItemsData.items;

  // 调整网格容器大小以保持正方形比例
  useEffect(() => {
    const handleResize = () => {
      if (gridWrapperRef.current) {
        const containerWidth = gridWrapperRef.current.clientWidth;
        setGridContainerSize({
          width: containerWidth,
          height: containerWidth
        });
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 初始生成网格
  useEffect(() => {
    generateBingoGrid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 监听验证码输入框的回车事件
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && showVerificationDialog) {
        verifyBingo();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showVerificationDialog, verificationInput]);

  // 验证框自动聚焦
  useEffect(() => {
    if (showVerificationDialog && verificationInputRef.current) {
      verificationInputRef.current.focus();
    }
  }, [showVerificationDialog]);

  // 生成随机验证码
  const generateVerificationCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  // 将字符串转换为数字哈希值
  const hashCode = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // 转换为32bit整数
    }
    return Math.abs(hash);
  };

  // 创建一个基于种子的确定性随机数生成器
  const createSeededRandom = (seed) => {
    let state = hashCode(seed);
    
    return function() {
      // 简单的线性同余发生器
      state = (state * 1664525 + 1013904223) % 4294967296;
      return state / 4294967296; // 返回0到1之间的数
    };
  };

  // 使用验证码作为种子来确定性地选择项目
  const getItemsFromSeed = (code, count) => {
    // 创建一个确定性的随机数生成器
    const seededRandom = createSeededRandom(code);
    
    // 复制原始项目数组
    const itemsCopy = [...bingoItems];
    
    // 使用Fisher-Yates洗牌算法，但使用我们的确定性随机函数
    for (let i = itemsCopy.length - 1; i > 0; i--) {
      const j = Math.floor(seededRandom() * (i + 1));
      [itemsCopy[i], itemsCopy[j]] = [itemsCopy[j], itemsCopy[i]];
    }
    
    // 返回洗牌后的前count个项目
    return itemsCopy.slice(0, count);
  };

  // 生成Bingo网格
  const generateBingoGrid = () => {
    setSelectedCells([]);
    setBingoLines([]);
    const newCode = generateVerificationCode();
    generateWithCode(newCode, gridSize);
  };

  // 使用特定验证码生成网格
  const generateWithCode = (code, size) => {
    setVerificationCode(code);
    const seedItems = getItemsFromSeed(code, size * size);
    setBingoGrid(seedItems);
  };

  // 切换格子的选择状态
  const toggleCellSelection = (index) => {
    const row = Math.floor(index / gridSize);
    const col = index % gridSize;
    const cellKey = `${row},${col}`;
    
    setSelectedCells(prev => {
      const cellIndex = prev.findIndex(cell => cell === cellKey);
      if (cellIndex === -1) {
        return [...prev, cellKey];
      } else {
        return prev.filter((_, i) => i !== cellIndex);
      }
    });
  };

  // 检查是否有新的Bingo线
  useEffect(() => {
    checkForBingo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCells, gridSize]);

  // 检查是否形成Bingo线 (水平、垂直和两个对角线)
  const checkForBingo = () => {
    // 创建一个网格来跟踪选中的单元格
    const grid = Array(gridSize).fill().map(() => Array(gridSize).fill(false));
    
    selectedCells.forEach(cell => {
      const [row, col] = cell.split(',').map(Number);
      grid[row][col] = true;
    });
    
    let newBingoFound = false;
    let newBingoLines = [];
    
    // 检查行
    for (let row = 0; row < gridSize; row++) {
      let rowComplete = true;
      for (let col = 0; col < gridSize; col++) {
        if (!grid[row][col]) {
          rowComplete = false;
          break;
        }
      }
      if (rowComplete) {
        const lineKey = `row-${row}`;
        newBingoLines.push(lineKey);
      }
    }
    
    // 检查列
    for (let col = 0; col < gridSize; col++) {
      let colComplete = true;
      for (let row = 0; row < gridSize; row++) {
        if (!grid[row][col]) {
          colComplete = false;
          break;
        }
      }
      if (colComplete) {
        const lineKey = `col-${col}`;
        newBingoLines.push(lineKey);
      }
    }
    
    // 检查主对角线 (左上到右下)
    let mainDiagComplete = true;
    for (let i = 0; i < gridSize; i++) {
      if (!grid[i][i]) {
        mainDiagComplete = false;
        break;
      }
    }
    if (mainDiagComplete) {
      newBingoLines.push('diag-main');
    }
    
    // 检查副对角线 (右上到左下)
    let antiDiagComplete = true;
    for (let i = 0; i < gridSize; i++) {
      if (!grid[i][gridSize - 1 - i]) {
        antiDiagComplete = false;
        break;
      }
    }
    if (antiDiagComplete) {
      newBingoLines.push('diag-anti');
    }
    
    // 检查是否有新的Bingo
    if (JSON.stringify(newBingoLines) !== JSON.stringify(bingoLines)) {
      newBingoFound = newBingoLines.some(line => !bingoLines.includes(line));
      setBingoLines(newBingoLines);
      
      // 如果找到新的Bingo，显示祝贺消息
      if (newBingoFound) {
        setShowCelebration(true);
        setTimeout(() => setShowCelebration(false), 3000);
      }
    }
  };

  // 下载Bingo网格
  const downloadBingoGrid = () => {
    if (bingoContainerRef.current) {
      html2canvas(bingoContainerRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        width: 1000,
        height: 1000,
        windowWidth: 1000,
        windowHeight: 1000
      }).then(canvas => {
        // Ensure the canvas is exactly 1000x1000
        const finalCanvas = document.createElement('canvas');
        finalCanvas.width = 1000;
        finalCanvas.height = 1000;
        const ctx = finalCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, 1000, 1000);
        
        // Draw the captured content centered and scaled to fit
        const scale = Math.min(1000 / canvas.width, 1000 / canvas.height);
        const x = (1000 - canvas.width * scale) / 2;
        const y = (1000 - canvas.height * scale) / 2;
        ctx.drawImage(canvas, x, y, canvas.width * scale, canvas.height * scale);
        
        const link = document.createElement('a');
        link.download = `melodys_bingo_${verificationCode}.png`;
        link.href = finalCanvas.toDataURL('image/png');
        link.click();
      });
    }
  };

  // 打开验证对话框
  const openVerificationDialog = () => {
    setShowVerificationDialog(true);
    setVerificationInput('');
    setVerificationResult({ message: '', isSuccess: false });
  };

  // 关闭验证对话框
  const closeVerificationDialog = () => {
    setShowVerificationDialog(false);
  };

  // 验证Bingo
  const verifyBingo = () => {
    const inputCode = verificationInput.toUpperCase();
    
    if (!inputCode) {
      setVerificationResult({ 
        message: '请输入生成种子', 
        isSuccess: false 
      });
      return;
    }

    // 使用用户输入的验证码生成Bingo网格
    generateWithCode(inputCode, gridSize);
    
    setVerificationResult({ 
      message: '好耶！已使用此种子重新生成相同的Bingo网格', 
      isSuccess: true 
    });
    
    // 3秒后关闭验证对话框
    setTimeout(() => {
      closeVerificationDialog();
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-base-200 flex items-center justify-center p-4">
      <div className="card bg-base-100 shadow-xl w-full max-w-4xl">
        <div className="card-body">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-primary">美丽大原神BINGO</h1>
          </div>
          
          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <select 
              className="select select-bordered select-primary flex-1"
              value={gridSize} 
              onChange={(e) => setGridSize(parseInt(e.target.value))}
            >
              <option value="3">3x3</option>
              <option value="4">4x4</option>
              <option value="5">5x5</option>
            </select>
            <button 
              className="btn btn-primary flex-1"
              onClick={generateBingoGrid}
            >
              生成Bingo
            </button>
            <button 
              className="btn btn-secondary flex-1"
              onClick={openVerificationDialog}
            >
              验证Bingo
            </button>
            <button 
              className="btn btn-accent flex-1"
              onClick={downloadBingoGrid}
            >
              保存为图片
            </button>
          </div>
          
          {/* Bingo Grid Container */}
          <div ref={bingoContainerRef} className="card bg-base-100 border-2 border-base-300 p-6">
            <h2 className="text-2xl font-bold text-center text-primary mb-4">
              美丽大原神BINGO挑战
            </h2>
            
            <div ref={gridWrapperRef} className="w-full mx-auto aspect-square max-h-[70vh]">
              <div 
                ref={bingoGridRef} 
                className="grid gap-3 bg-base-200 p-4 rounded-lg h-full relative"
                style={{ 
                  gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
                  gridTemplateRows: `repeat(${gridSize}, 1fr)`,
                  width: gridContainerSize.width,
                  height: gridContainerSize.height
                }}
              >
                {bingoGrid.map((item, index) => {
                  const row = Math.floor(index / gridSize);
                  const col = index % gridSize;
                  const isSelected = selectedCells.includes(`${row},${col}`);
                  
                  return (
                    <div 
                      key={index}
                      className={`
                        card bg-base-100 border-2 transition-all cursor-pointer
                        hover:shadow-lg hover:-translate-y-1 flex items-center justify-center
                        text-center p-2 ${isSelected ? 'bg-primary text-primary-content border-primary' : 'border-base-300'}
                      `}
                      onClick={() => toggleCellSelection(index)}
                    >
                      <span className="text-xs sm:text-sm break-words">
                        {item}
                      </span>
                      {isSelected && (
                        <span className="absolute top-1 right-2 text-lg">✓</span>
                      )}
                    </div>
                  );
                })}
                
                {/* Bingo Lines */}
                {bingoLines.map(line => {
                  if (line.startsWith('row-')) {
                    const row = parseInt(line.split('-')[1]);
                    return (
                      <div 
                        key={line} 
                        className="absolute bg-secondary rounded-full pointer-events-none"
                        style={{ 
                          height: '5px',
                          width: 'calc(100% - 32px)',
                          left: '16px',
                          top: `${(row + 0.5) * (100 / gridSize)}%`,
                          transform: 'translateY(-50%)'
                        }}
                      />
                    );
                  } else if (line.startsWith('col-')) {
                    const col = parseInt(line.split('-')[1]);
                    return (
                      <div 
                        key={line} 
                        className="absolute bg-secondary rounded-full pointer-events-none"
                        style={{ 
                          width: '5px',
                          height: 'calc(100% - 32px)',
                          top: '16px',
                          left: `${(col + 0.5) * (100 / gridSize)}%`,
                          transform: 'translateX(-50%)'
                        }}
                      />
                    );
                  } else if (line === 'diag-main') {
                    return (
                      <div 
                        key={line} 
                        className="absolute bg-secondary rounded-full pointer-events-none"
                        style={{ 
                          width: '5px',
                          height: '140%',
                          top: '-20%',
                          right: '50%',
                          transform: 'rotate(-45deg)',
                          transformOrigin: '100% 50%'
                        }}
                      />
                    );
                  } else if (line === 'diag-anti') {
                    return (
                      <div 
                        key={line} 
                        className="absolute bg-secondary rounded-full pointer-events-none"
                        style={{ 
                          width: '5px',
                          height: '140%',
                          top: '-20%',
                          left: '50%',
                          transform: 'rotate(45deg)',
                          transformOrigin: '0% 50%'
                        }}
                      />
                    );
                  }
                  return null;
                })}
              </div>
            </div>
            
            {/* Footer Info */}
            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm gap-2">
              <div className="font-mono">
                验证码: {verificationCode}<br />
                Version: EggTart
              </div>
              <div className="text-right text-xs opacity-70">
                © 2015-2025 花子@3ON EM<br />
                HTTPS://XMY.0M0.NEWS
              </div>
            </div>
          </div>
        </div>
        
        {/* Verification Modal */}
        {showVerificationDialog && (
          <div className="modal modal-open z-50" onClick={closeVerificationDialog}>
            <div className="modal-box z-50" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-bold text-lg text-primary mb-4">验证Bingo</h3>
              <input
                ref={verificationInputRef}
                type="text"
                placeholder="输入BINGO种子(验证码)"
                className="input input-bordered input-primary w-full mb-4"
                value={verificationInput}
                onChange={(e) => setVerificationInput(e.target.value)}
              />
              {verificationResult.message && (
                <div className={`alert ${verificationResult.isSuccess ? 'alert-success' : 'alert-error'} mb-4`}>
                  <span>{verificationResult.message}</span>
                </div>
              )}
              <div className="modal-action">
                <button className="btn btn-primary" onClick={verifyBingo}>生成</button>
                <button className="btn" onClick={closeVerificationDialog}>取消</button>
              </div>
            </div>
          </div>
        )}
        
        {/* Celebration Toast */}
        {showCelebration && (
          <div className="toast toast-center">
            <div className="alert alert-success">
              <span className="text-2xl font-bold">𝘽𝙄𝙉𝙂𝙊!</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;